/**
 * Validation Utilities
 * 
 * This file contains regex patterns and validation functions for common data types
 * like PAN, Email, Phone Number, and GST Number that can be used across the application.
 */

/**
 * Regex patterns for validation
 */
export const VALIDATION_PATTERNS = {
  /**
   * PAN (Permanent Account Number) - Indian tax identification number
   * Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)
   */
  PAN: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,

  /**
   * Email address validation
   * Standard email format validation
   */
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,

  /**
   * Phone Number - Indian phone numbers
   * Supports formats: +91XXXXXXXXXX, 91XXXXXXXXXX, 0XXXXXXXXXX, XXXXXXXXXX
   * Validates 10-digit mobile numbers
   */
  PHONE_NUMBER: /^(\+91|91|0)?[6-9]\d{9}$/,

  /**
   * GST Number (GSTIN) - Indian Goods and Services Tax Identification Number
   * Format: 15 characters - 2 state code, 10 PAN, 1 entity number, 1 Z, 1 check digit
   * Example: 27ABCDE1234F1Z5
   */
  GST_NUMBER: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
};

/**
 * Validation functions
 */
export class ValidationUtils {
  /**
   * Validates PAN number
   * @param pan - PAN number to validate
   * @returns true if valid, false otherwise
   */
  static isValidPAN(pan: string): boolean {
    if (!pan || typeof pan !== 'string') {
      return false;
    }
    return VALIDATION_PATTERNS.PAN.test(pan.toUpperCase().trim());
  }

  /**
   * Validates email address
   * @param email - Email address to validate
   * @returns true if valid, false otherwise
   */
  static isValidEmail(email: string): boolean {
    if (!email || typeof email !== 'string') {
      return false;
    }
    return VALIDATION_PATTERNS.EMAIL.test(email.trim());
  }

  /**
   * Validates phone number (Indian format)
   * @param phone - Phone number to validate
   * @returns true if valid, false otherwise
   */
  static isValidPhoneNumber(phone: string): boolean {
    if (!phone || typeof phone !== 'string') {
      return false;
    }
    // Remove spaces, dashes, and other common separators
    const cleanedPhone = phone.replace(/[\s\-\(\)]/g, '');
    return VALIDATION_PATTERNS.PHONE_NUMBER.test(cleanedPhone);
  }

  /**
   * Validates GST number
   * @param gst - GST number to validate
   * @returns true if valid, false otherwise
   */
  static isValidGSTNumber(gst: string): boolean {
    if (!gst || typeof gst !== 'string') {
      return false;
    }
    return VALIDATION_PATTERNS.GST_NUMBER.test(gst.toUpperCase().trim());
  }

  /**
   * Parent validation function that routes to the appropriate validator based on type
   * @param value - The value to validate
   * @param type - The type of validation to perform ('pan', 'email', 'phone', 'phone_number', 'gst', 'gst_number')
   * @returns true if valid, false otherwise
   */
  static validate(value: string, type: ValidationType): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }

    switch (type.toLowerCase()) {
      case 'pan':
        return this.isValidPAN(value);
      
      case 'email':
        return this.isValidEmail(value);
      
      case 'phone':
      case 'phone_number':
        return this.isValidPhoneNumber(value);
      
      case 'gst':
      case 'gst_number':
        return this.isValidGSTNumber(value);
      
      default:
        console.warn(`Unknown validation type: ${type}`);
        return false;
    }
  }
}

/**
 * Convenience functions for direct validation
 */
export const isValidPAN = (pan: string): boolean => ValidationUtils.isValidPAN(pan);
export const isValidEmail = (email: string): boolean => ValidationUtils.isValidEmail(email);
export const isValidPhoneNumber = (phone: string): boolean => ValidationUtils.isValidPhoneNumber(phone);
export const isValidGSTNumber = (gst: string): boolean => ValidationUtils.isValidGSTNumber(gst);

/**
 * Validation type options
 */
export type ValidationType = 'pan' | 'email' | 'phone' | 'phone_number' | 'gst' | 'gst_number';

/**
 * Parent validation function that routes to the appropriate validator based on type
 * @param value - The value to validate
 * @param type - The type of validation to perform ('pan', 'email', 'phone', 'phone_number', 'gst', 'gst_number')
 * @returns true if valid, false otherwise
 */
export function validate(value: string, type: ValidationType): boolean {
  return ValidationUtils.validate(value, type);
}

